import React, {useEffect} from 'react'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell, {tableCellClasses} from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import {styled} from '@mui/material/styles'
import useEvaluationSessions from '../../hooks/useEvaluationSessions'
import useUser from '../../hooks/useUser'
import ItemLoader, {RendererProps} from '../../components/ItemLoader'
import Page from '../../components/Page'
import EvalRuns from '../../components/EvalRuns'
import {EvaluationFilters} from '../../components/filters'
import ModelSummary from '../../components/models/Summary'
import {getModel, updateModel} from '../../services/models'
import type {Model as ModelType, Eval} from '../../services/types'
import CallToAction from '../../components/CallToAction'
import Path from '../../routeLinks'
import {NavLink} from 'react-router-dom'

export const fetchModel = getModel

export const RunCell = styled(TableCell)(({theme}) => ({
  [`&.${tableCellClasses.head}, &.${tableCellClasses.body}`]: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 'larger',
  },
  [`&.${tableCellClasses.body}`]: {
    border: 0,
  },
}))
const LatestRuns = ({evals}: {evals?: Eval[]}) => (
  <TableContainer>
    <Table>
      <TableHead>
        <TableRow>
          <RunCell>Evaluation</RunCell>
          <RunCell align="center">Date Run</RunCell>
          <RunCell align="center">Score</RunCell>
          <RunCell align="center">Cost to Run</RunCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {evals?.map(({id, evaluation_id, evaluation_name, datetime_completed, score, price}) => (
          <TableRow key={id}>
            <RunCell component="td">
              <Button
                component={NavLink}
                to={Path.evaluations.show(evaluation_id)}
                sx={{
                  width: 'fit-content',
                  textTransform: 'inherit',
                  fontWeight: 700,
                  fontSize: 'larger',
                }}
              >
                {evaluation_name}
              </Button>
            </RunCell>
            <RunCell align="center" component="td">
              {datetime_completed?.slice(0, 10)}
            </RunCell>
            <RunCell align="center" component="td">
              {score?.toFixed(1)}
            </RunCell>
            <RunCell align="center" component="td">
              ${Math.max(1, (price || 0) / 100).toFixed(2)}
            </RunCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

const ShowModel = ({item: model, setItem: setModel}: RendererProps<ModelType>) => {
  const {canEdit} = useUser()
  const {items: evalSessions, refresh} = useEvaluationSessions()

  useEffect(() => {
    if (model) {
      refresh({
        filters: {
          models: [model],
          latest: true,
          fields: ['price', 'evaluation_id', 'evaluation_name', 'datetime_completed', 'score'],
        },
      })
    }
  }, [model, refresh])

  const saveModel = async (m: ModelType) => {
    if ((await updateModel(m)) === 'Model updated') {
      setModel({...model, ...m})
    }
  }

  return (
    <Page title={`${model.publisher}${model.publisher ? ' | ' : ''}${model.name}`} showTitle>
      <Stack spacing={5} sx={{mt: 10, mb: 5, pr: 4}}>
        <ModelSummary model={model} updateModel={canEdit(model) ? saveModel : undefined} />

        <Typography variant="h3" sx={{color: 'primary.main'}}>
          Latest Runs
        </Typography>
        <LatestRuns evals={evalSessions} />

        <CallToAction
          title="Compare This Model’s Performance"
          actionLabel="Connect Now"
          action={Path.models.create()}
        >
          <Typography align="center" sx={{color: 'white', fontWeight: 700}}>
            Connect your own model to see how it stacks up against
            {model.publisher ? ` ${model.publisher}'s ` : ''}
            Open AI’s
            {model.name}
          </Typography>
        </CallToAction>

        <EvalRuns
          models={[model]}
          searchOptions={{
            filtersElem: EvaluationFilters,
            searchLabel: 'filter evaluations',
            sortLabel: 'Sort by:',
            sortOptions: {
              topAvg: 'Top average score',
              bottomAvg: 'Bottom average score',
              nameAsc: 'Model name (asc)',
              nameDesc: 'Model name (desc)',
              publisherAsc: 'Publisher (asc)',
              publisherDesc: 'Publisher (desc)',
            },
          }}
        />
      </Stack>
    </Page>
  )
}

const ModelView: React.FC = () => <ItemLoader itemName="model" renderer={ShowModel} />
export default ModelView
